import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, Select, MenuItem, CircularProgress, Box, TextField,
  InputAdornment, Collapse, IconButton, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

const STATUS_OPTIONS = ['pending', 'paid', 'shipped', 'cancelled'];
const statusLabel = (s) => ({ pending: 'In attesa', paid: 'Pagato', shipped: 'Spedito', cancelled: 'Annullato', ebay_redirect: 'eBay' }[s] || s);
const statusColor = (s) => ({ pending: 'warning', paid: 'success', shipped: 'info', cancelled: 'error' }[s] || 'default');
const methodLabel = (m) => ({ stripe: 'Carta', bank_transfer: 'Bonifico', ebay: 'eBay' }[m] || m);

function OrderRow({ o, updating, onStatusChange }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{new Date(o.created_at).toLocaleDateString('it-IT')}</Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 220 }}>
          <Typography variant="body2" noWrap title={o.item_title}>{o.item_title}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">{o.shipping_name || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{o.shipping_email}</Typography>
          {o.shipping_phone && <Typography variant="caption" color="text.secondary" display="block">{o.shipping_phone}</Typography>}
        </TableCell>
        <TableCell>
          <Typography fontWeight="bold">€{(o.amount - (o.discount_amount || 0)).toFixed(2)}</Typography>
          {o.discount_amount > 0 && (
            <Typography variant="caption" color="success.main">−€{o.discount_amount.toFixed(2)}</Typography>
          )}
        </TableCell>
        <TableCell>{methodLabel(o.payment_method)}</TableCell>
        <TableCell>
          <Select size="small" value={o.payment_status} disabled={updating === o.id}
            onChange={e => onStatusChange(o.id, e.target.value)}
            renderValue={v => <Chip label={statusLabel(v)} color={statusColor(v)} size="small" />}>
            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
          </Select>
        </TableCell>
      </TableRow>

      {/* Riga espandibile con dettagli spedizione */}
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'grey.50' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">Dettagli spedizione</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Nome</Typography>
                  <Typography variant="body2">{o.shipping_name || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">
                    <a href={`mailto:${o.shipping_email}`} style={{ color: 'inherit' }}>
                      {o.shipping_email}
                    </a>
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Telefono</Typography>
                  <Typography variant="body2">{o.shipping_phone || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Indirizzo</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{o.shipping_address || '—'}</Typography>
                </Box>
              </Box>

              {o.notes && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">Note</Typography>
                  <Typography variant="body2">{o.notes}</Typography>
                </>
              )}

              {o.payment_method === 'bank_transfer' && o.payment_status === 'pending' && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      In attesa di bonifico — €{(o.amount - (o.discount_amount || 0)).toFixed(2)}
                    </Typography>
                    <Typography variant="caption">
                      Causale attesa: {o.item_title}
                    </Typography>
                  </Box>
                </>
              )}

              <Box sx={{ mt: 1.5 }}>
                <Chip
                  icon={<EmailIcon />}
                  label={`Scrivi a ${o.shipping_email}`}
                  size="small"
                  component="a"
                  href={`mailto:${o.shipping_email}?subject=Ordine: ${o.item_title}`}
                  clickable
                  variant="outlined"
                />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminOrdersPage() {
  const { authHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orders/admin`, { headers: authHeader() })
      .then(r => setOrders(r.data.orders || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, payment_status) => {
    setUpdating(id);
    try {
      const { data } = await axios.patch(`${API}/orders/admin/${id}`, { payment_status }, { headers: authHeader() });
      setOrders(prev => prev.map(o => o.id === id ? data.order : o));
    } catch { }
    setUpdating(null);
  };

  const filtered = orders.filter(o =>
    !search
    || o.item_title?.toLowerCase().includes(search.toLowerCase())
    || o.shipping_email?.toLowerCase().includes(search.toLowerCase())
    || o.shipping_name?.toLowerCase().includes(search.toLowerCase())
    || o.shipping_address?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Container sx={{ pt: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Ordini</Typography>

      <TextField size="small" placeholder="Cerca per articolo, email, nome..." value={search}
        onChange={e => setSearch(e.target.value)} sx={{ mb: 3, width: 320 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />

      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={40} />
              <TableCell>Data</TableCell>
              <TableCell>Articolo</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Importo</TableCell>
              <TableCell>Metodo</TableCell>
              <TableCell>Stato</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Nessun ordine</TableCell></TableRow>
            )}
            {filtered.map(o => (
              <OrderRow key={o.id} o={o} updating={updating} onStatusChange={updateStatus} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}
